/**
 * `ShellData` — everything the root layout read straight out of SQLite, named
 * once, as a type.
 *
 * ── Read this before you build on the shell ──
 *
 * `excellent/app/layout.tsx` is an ASYNC SERVER COMPONENT. Before it renders a
 * single element it performs ~12 synchronous better-sqlite3 reads (plus one
 * write) and threads the results into the chrome as props. None of that can
 * survive the move to an SPA: `server-only` is no longer a compile wall, and
 * every one of those modules reaches the vault (PRD §5 I4 — the highest-risk
 * item in the PRD, and the reason R1 exists).
 *
 * So the port SPLITS the layout in two:
 *
 *   - its STRUCTURE and its PURE logic — the provider stack, the chrome DOM, the
 *     redirect cascade, the theme-class composition — port now, and live in
 *     `shell.tsx` / `redirects.ts` / `theme.ts`;
 *   - its DATA becomes this interface, supplied over HTTP.
 *
 * **The endpoint is `GET /api/shell` (RO-11-01).** Contract:
 * `packages/ui/src/api/contracts/shell.ts`; handler: `server/routes/shell.ts`.
 * `SHELL_DATA_FALLBACK` remains the offline/error seed so a failed boot fetch
 * still paints chrome-less defaults rather than crashing the SPA.
 *
 * This is deliberately one flat interface rather than several: the layout read
 * it all in one server pass, so one `GET /api/shell` request preserves that
 * shape and keeps the SPA's first paint to a single round trip.
 */

import type { ShellGates } from "./redirects";

/** `resolveScopeFromPath()` — the active project scope, DERIVED FROM THE URL. */
export type ShellScope = { kind: "all" } | { kind: "project"; slug: string; name: string };

/** One enabled section row for the rails (`listWorkspace|ProjectSectionStates`). */
export interface ShellSection {
  slug: string;
  name: string;
}

/** A project in the TopNav scope switcher (`loadRailScope`). */
export interface ShellScopeProject {
  id: number;
  slug: string;
  name: string;
  group_id: number | null;
  parent_id: number | null;
  role: string | null;
}

/** A folder row for the TopNav switcher (`loadRailScope` groups). */
export interface ShellFolder {
  id: number;
  name: string;
}

export interface ShellData extends ShellGates {
  /** `resolveTenant()` → the workspace the chrome reads against. */
  workspaceId: number | null;

  /** `getUserSetting("appearance.theme")` + the `theme` cookie fast path. */
  theme: {
    /** Raw `appearance.theme`: "light" | "dark" | "system" | legacy composite. */
    dbTheme: string;
    /** `appearance.accent` — normalized by `theme.ts`, brand indigo when unset. */
    accent: string;
  };

  /** `resolveWorkspaceLexicon(workspaceId)` — C15 grounded vocabulary, or null. */
  lexicon: unknown | null;

  /** `loadReviewCount()` — the TopNav review badge. */
  reviewCount: number;

  /** `pulseSelfNavEnabled()` / `pulseDynamicEnabled()` — ModeRail instruments. */
  pulse: { enabled: boolean; dynamic: boolean };

  /** `isCopilotEnabled()` / `isCopilotDevelopEnabled()` — the dock's chat surface. */
  copilot: { enabled: boolean; develop: boolean };

  /** Enabled sections for the current scope + `SECTION_REGISTRY.length`. */
  rail: { sections: ShellSection[]; totalSections: number };

  /** The PTY port the desktop wrapper actually bound (was an inline `<script>`). */
  ptyPort: number;

  // ── Additive TopNav fields (`loadRailScope`) — RO-11-01 ──────────────────
  /** Recently-opened + pinned projects for the switcher. */
  projects: ShellScopeProject[];
  /** Full workspace catalog (slug+name) for switcher search. */
  allProjects: { slug: string; name: string }[];
  /** Workspace folders for switcher grouping. */
  groups: ShellFolder[];
  /** Active workspace display name (switcher header). */
  activeWorkspaceName: string;
  /** Default/home project slug, or null. */
  defaultSlug: string | null;
  /** Workspaces the signed-in user can switch into (org dropdown). */
  workspaces: { id: number; slug: string; name: string }[];
}

/**
 * The zero state. Chrome that has no data renders as if the feature is off —
 * never as if it is broken, and never with invented values: `reviewCount: 0` is
 * the same fallback `loadReviewCount()` uses on error, and both pulse flags and
 * both copilot flags default OFF exactly as their config seams do when unset.
 *
 * `onboardingActive` / `needsWorkspace` default FALSE so a failed boot fetch
 * does not bounce the operator into onboarding incorrectly. When `GET /api/shell`
 * succeeds, the live values replace these defaults before the first render.
 */
export const SHELL_DATA_FALLBACK: ShellData = {
  onboardingActive: false,
  needsWorkspace: false,
  workspaceId: null,
  theme: { dbTheme: "", accent: "" },
  lexicon: null,
  reviewCount: 0,
  pulse: { enabled: false, dynamic: false },
  copilot: { enabled: false, develop: false },
  rail: { sections: [], totalSections: 0 },
  ptyPort: 3001,
  projects: [],
  allProjects: [],
  groups: [],
  activeWorkspaceName: "Workspace",
  defaultSlug: null,
  workspaces: [],
};

/**
 * The live shell data, readable OUTSIDE React.
 *
 * The redirect cascade runs in the root route's `beforeLoad` — a router hook,
 * not a component — and two of its gates need server state (`onboardingActive`,
 * `needsWorkspace`). A React context cannot reach there, so the data lives in a
 * module holder that `main.tsx` seeds once at boot.
 *
 * Seeded in `main.tsx` via `setShellData(await apiCall(shell.get, {}))` before
 * `createRoot` — the one-line seam this module was designed for.
 */
let currentShellData: ShellData = SHELL_DATA_FALLBACK;

export function getShellData(): ShellData {
  return currentShellData;
}

export function setShellData(data: ShellData): void {
  currentShellData = data;
}
